import {castArray, upsertArrayItem} from '@coolcolduk/util';
import type {ProgressDb, ProgressStudy} from '@cp/model';
import {mapProgressWriteToDb} from '@cp/model';
import {getDoc, serverTimestamp, setDoc} from 'firebase/firestore';
import getProgressDocRef from './getProgressDocRef';

export interface AddStudyProgressInput {
  examModuleId: string;
  topic: string;
  titles?: string[];
  done?: boolean;
  progress?: number;
}

export default async function addStudyProgressDoc(
  uid: string,
  {examModuleId, topic, titles, done, progress}: AddStudyProgressInput,
): Promise<void> {
  const ref = getProgressDocRef(uid, examModuleId);
  const snap = await getDoc(ref);
  const existingRaw = snap.data() as ProgressDb | undefined;

  const existingStudy: ProgressStudy[] = existingRaw?.study ?? [];
  const matchByTopic = (s: ProgressStudy) => s.topic === topic;
  const existing = existingStudy.find(matchByTopic);

  const existingTitles = existing?.title ?? [];
  const titlesArray = titles === undefined ? existingTitles : [...existingTitles, ...castArray(titles)];
  const mergedTitles = Array.from(new Set(titlesArray));

  const updatedEntry: ProgressStudy = {
    topic,
    title: mergedTitles,
    done: done ?? existing?.done ?? false,
    progress: progress ?? existing?.progress,
  };

  const updatedStudy = upsertArrayItem(existingStudy, updatedEntry, matchByTopic);

  // If this is a new document, use the shared helper to create the initial ProgressDb shape.
  if (!existingRaw) {
    const payload: ProgressDb = mapProgressWriteToDb(
      {
        scores: [],
        study: updatedStudy,
      },
      serverTimestamp() as unknown as ProgressDb['lastUpdated'],
    );
    await setDoc(ref, payload, {merge: false});
    return;
  }

  await setDoc(ref, {study: updatedStudy, lastUpdated: serverTimestamp()}, {merge: true});
}
